/**
 * appInstall.ts - Where "Get the app" sends a visitor.
 *
 * Viewport width decides WHERE the install control sits; the device platform
 * decides WHAT a click does. Width alone can't tell an iPad from a laptop, so
 * the platform comes from the user agent plus touch support.
 */

// Mirrors client/lib/storeBadges.tsx in the main website. The two apps build
// separately, so the chat app keeps its own copy — keep them in step.
export const APP_STORE_URL = 'https://apps.apple.com/app/id6444813706'
export const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=org.mushtarek'

// The badge artwork is served by the main website at /static/badges/. In prod the
// chat app shares that origin; on localhost the website runs on :3000.
const siteOrigin =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? 'http://localhost:3000'
        : ''

// English on every language, and width/height are the aspect ratio only — see
// storeBadges.tsx for why.
export const STORE_BADGES = [
    { href: APP_STORE_URL, src: `${siteOrigin}/static/badges/download-on-the-app-store.svg`, alt: 'Download on the App Store', width: 120, height: 40 },
    { href: GOOGLE_PLAY_URL, src: `${siteOrigin}/static/badges/get-it-on-google-play.png`, alt: 'Get it on Google Play', width: 134, height: 40 },
]

export type Platform = 'ios' | 'android' | 'desktop' | 'unknown'

export const detectPlatform = ({ userAgent, maxTouchPoints }: { userAgent: string; maxTouchPoints: number }): Platform => {
    const ua = userAgent || ''
    if (/iPhone|iPad|iPod/.test(ua)) return 'ios'
    // iPadOS 13+ Safari reports a Mac user agent; no real Mac has a touch screen.
    if (/Macintosh/.test(ua) && maxTouchPoints > 1) return 'ios'
    if (/Android/i.test(ua)) return 'android'
    if (/Windows NT|Macintosh|CrOS|X11|Linux/.test(ua)) return 'desktop'
    return 'unknown'
}

export const currentPlatform = (): Platform =>
    typeof navigator === 'undefined'
        ? 'unknown'
        : detectPlatform({ userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints || 0 })

export type InstallAction =
    | { kind: 'navigate'; url: string }
    | { kind: 'qr' }
    | { kind: 'choose' }

export const installAction = (platform: Platform): InstallAction => {
    if (platform === 'ios') return { kind: 'navigate', url: APP_STORE_URL }
    if (platform === 'android') return { kind: 'navigate', url: GOOGLE_PLAY_URL }
    if (platform === 'desktop') return { kind: 'qr' }
    return { kind: 'choose' }
}

// Only Mushtarek has an app, and this viewer serves every white-label. Same rule
// as the landing gate (client/pages/mushtarek-landing/gate.ts): staging/local
// always, production Mushtarek only, judged by the host OR the instance (from the
// public /ua/info), so a Mushtarek deployment on another hostname still qualifies.
export const canShowAppInstall = (host: string, instance?: { instanceName?: string; domainName?: string }): boolean => {
    const h = (host || '').toLowerCase()
    if (h.startsWith('localhost') || h.startsWith('127.') || h.startsWith('192.') || h.startsWith('staging')) return true
    const instanceName = String(instance?.instanceName || '').toLowerCase()
    const domainName = String(instance?.domainName || '').toLowerCase()
    return h.includes('mushtarek') || instanceName.includes('mushtarek') || domainName.includes('mushtarek')
}

// The public page the QR code opens: store redirect on phones, both badges elsewhere.
export const DOWNLOAD_APP_PATH = 'download-app'
export const downloadAppUrl = (origin: string, base: string): string =>
    `${origin}${base.endsWith('/') ? base : `${base}/`}#/${DOWNLOAD_APP_PATH}`

// The bottom bar's dismissal lasts for the browsing session. Storage can be
// blocked (private mode, embedded contexts), which just means "not dismissed".
const DISMISS_KEY = 'appInstallBarDismissed'

export const isInstallBarDismissed = (): boolean => {
    try {
        return sessionStorage.getItem(DISMISS_KEY) === '1'
    } catch {
        return false
    }
}

export const dismissInstallBar = (): void => {
    try {
        sessionStorage.setItem(DISMISS_KEY, '1')
    } catch {
        // Blocked storage: the bar stays hidden until reload, which is fine.
    }
}
